import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import produce from 'immer';
import { debounce } from 'lodash';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';
import { FC, useCallback } from 'react';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import useTableState from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { useListExternalIntegrationLogsQuery } from 'dashboard/redux/api/external-integration-log';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { Iso8601DateString } from 'lib/types';

const DashboardIntegrationLogsView: FC = () => {
  const {
    tableState: { setFilter, filter, currentPage, pageSize, setPageSize, setCurrentPage },
  } = useTableState<{
    omni?: string;
    'created-at-start'?: Iso8601DateString;
    'created-at-end'?: Iso8601DateString;
    'creator-id'?: string;
    'related-object-ids'?: string[];
  }>({});
  const { 'created-at-start': createdAtStart, 'created-at-end': createdAtEnd } = filter;
  const hasUsedFilters = !Object.values(filter).every(isEmpty);

  const {
    data: { data: logs = [], meta: paginationParams } = {},
    isFetching,
    error,
  } = useListExternalIntegrationLogsQuery({
    filter: filter,
    page: {
      number: currentPage,
      size: pageSize,
    },
  });

  useRTKErrorHandler({
    error,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const handleOmniInput = useCallback(
    (value: string) =>
      setFilter(
        produce(filter, (draft) => {
          draft.omni = value || '';
        }),
      ),
    [filter, setFilter],
  );

  const filterConfig: FilterConfig[] = [
    {
      id: 'dateRange',
      name: `Created At (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
      type: 'daterange',
      onChange: (range: RangeValue<DateValue> | null) => {
        setFilter(
          produce(filter, (draft) => {
            draft['created-at-start'] = range?.start.toString();
            draft['created-at-end'] = range?.end.toString();
          }),
        );
      },
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
      datePickerProps: {
        granularity: 'minute',
      },
    },
  ];

  return (
    <>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Integration Logs' />
        <Section>
          <Card>
            <Card.Body>
              <TableControls
                isLoading={isFetching}
                defaultShowFilters
                searchProps={{
                  id: 'integration-logs-table__search',
                  handleInput: debounce(handleOmniInput, 500),
                  value: filter.omni || '',
                  placeholder: 'Integration Log (eil_) or object ID (i.e. inq_, ver_)',
                }}
                filters={filterConfig}
              />
              <TableLayout
                isLoading={isFetching}
                isEmpty={logs.length === 0}
                showHeader={hasUsedFilters}
                headerTR={
                  <>
                    <TableTH>ID</TableTH>
                    <TableTH>Creator</TableTH>
                    <TableTH>Response Status</TableTH>
                    <TableTH>Status</TableTH>
                    <TableTH>{`Created at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                  </>
                }
                nullState={<TableNullState hasUsedFilters={hasUsedFilters} />}
              >
                {logs.map((log) => (
                  <TableLinkTR
                    to={`/dashboard/integration-logs/${log.id}`}
                    key={`integration-log-table__row-${log.id}`}
                    id={`integration-log-table__row-${log.id}`}
                  >
                    <TableTD>
                      <MonoSpan>{log.id}</MonoSpan>
                    </TableTD>
                    <TableTD>
                      {log.relationships.creator.data?.id ? (
                        <MonoSpan>{log.relationships.creator.data.id}</MonoSpan>
                      ) : (
                        <Sticker>Archived</Sticker>
                      )}
                    </TableTD>
                    <TableTD>
                      <Sticker
                        variant={
                          log.attributes['response-status-code'] === '200' ||
                          ['200', '201'].includes(log.attributes['response-status-code'])
                            ? 'success'
                            : 'danger'
                        }
                      >
                        {log.attributes['response-status-code']}
                      </Sticker>
                    </TableTD>
                    <TableTD>
                      <Sticker variant={log.attributes.status === 'created' ? 'green' : 'yellow'}>
                        {log.attributes.status}
                      </Sticker>
                    </TableTD>
                    <TableTD>
                      {DateTime.fromISO(log.attributes['created-at']).toFormat(
                        DATE_AND_TIME_FORMAT,
                      )}
                    </TableTD>
                  </TableLinkTR>
                ))}
              </TableLayout>
              <Pagination
                isLoading={isFetching}
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={setPageSize}
                handlePageLinkClick={setCurrentPage}
              />
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardIntegrationLogsView;
